import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PriceContent from 'app/components/PriceContent';
import TabApm from 'app/components/TabApm';
import TabCredit from 'app/components/TabCredit';
import TabBankTransfer from 'app/components/TabBankTransfer';
import { GlobalCard, Scheme } from './data';
import { usePaymentState } from 'payment_state/PaymentStateContext';
import { useMutation } from 'react-query';
import { innerSearch, paymentMethodList } from 'api/Api';
import { MethodListRequest } from './types';
import { isMobile } from 'react-device-detect';
import { findImage, isEmptyObject } from 'utils/Utils';
import LoadingIndicator from 'app/components/LoadingIndicator';
import IframeBridge from 'IframeBridge';
import { PopupReturnBridge } from 'app/components/PopupReturnBridge';
import TabGlobal from 'app/components/TabGlobal';
import { ApplePayForm } from 'app/components/PV01Form/types';
import useModals from 'app/components/Modal/manager/useModal';
import CustomModal from 'app/components/Modal/CustomModal';
import TermsModal from 'app/components/Modal/TermsModal';
import PrivacyPolicyModal from 'app/components/Modal/PrivacyPolicyModal';
import { RequestPVKRForm } from 'app/components/CreditKoreaForm/types';
import ErrorModal from 'app/components/Modal/ErrorModal';
import ArrowPng from 'app/assets/image/common/arrow.png';
import { useMediaQuery } from 'react-responsive';

type BridgeEvent = {
  type: 'POPUP_REDIRECT';
  url: string;
  payload: Record<string, string | null>;
};

const allowedChildOrigins = [`${window.location.origin}`];

const PaymentPage = () => {
  let checkPopupTimer;
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const foucsTerms = useRef<HTMLDivElement>(null);
  const { paymentState, setPaymentState, setShopifyState } = usePaymentState();
  const { openModal } = useModals();
  const [popup, setPopup] = useState<Window | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [language, setLanguage] = useState<string>('');
  const [langUse, setLangUse] = useState<string>();
  const [tid, setTid] = useState<string>('');
  const [card, setCard] = useState<GlobalCard[]>([]);
  const [bankTransferList, setBankTransferList] = useState<{
    [key: string]: Scheme[];
  }>({});
  const [agreeCheck, setAgreeCheck] = useState<boolean>(false);
  const [AgreeCheckError, setAgreeCheckError] = useState<boolean>(false);
  const [koreanCardList, setKoreanCardList] = useState<Scheme[]>([]);
  const [vietnamCardList, setVietnamCardList] = useState<Scheme[]>([]);
  const [apmList, setApmList] = useState<{ [key: string]: Scheme[] }>({});
  const [selectScheme, setSelectScheme] = useState<Scheme>({
    schemeDisplayNm: '',
    partnerCurr: '',
    partnerCd: '',
    schemeCd: '',
    schemeGroup: '',
    approvalAmount: 0,
  });
  const [globalPayment, setGlobalPayment] = useState<Scheme[]>();
  const [isGlobal, setIsGlobal] = useState<boolean>(false);
  const [paymentData, setPaymentData] = useState<any>();
  const [filterdPaymentData, setFilterdPaymentData] = useState<any>();
  const [keypadToken, setKeypadToken] = useState<string>('');
  const [selectedPayment, setSelectedPayment] = useState<
    'credit' | 'apm' | 'bank' | 'global'
  >();
  const [isWeChat, setIsWeChat] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);

  const [paymentRequestBody, setPaymentRequestBody] =
    useState<MethodListRequest>();
  const isMobileSize = useMediaQuery({ maxWidth: 767 });
  const returnType = window.sessionStorage.getItem('returnType');

  const getPaymentList = useMutation(
    () => paymentMethodList(paymentRequestBody),
    {
      onSuccess: data => {
        if (String(data?.data?.resultStatus).toUpperCase() === 'SUCCESS') {
          setPaymentData(data?.data?.paymentMethod);
          setKeypadToken(data.data.keypadToken);
          setLanguage(String(data.data.langCd).toLocaleLowerCase());
          setLangUse(data.data.langCdUse);
          setIsLoading(false);
          setTid(data.data.tid);
        } else {
          setPaymentState(prevData => ({
            ...prevData,
            resultCode: data.data.resultCode,
            resultMessage: data.data.resultMessage,
            tid: data.data.tid,
          }));
          navigate('/error');
        }
      },
      onError: error => {
        navigate('/error');
      },
    },
  );

  // 결제 결과 PaymentState set 및 결과화면 처리
  const payResult = data => {
    // 결과 상태
    const resultStatus = String(data.data.response.resultStatus).toLowerCase();
    // 결제 타입
    const payType = String(data.data.display.payType).toLowerCase();

    // InnerSearch 결과 값 PaymentState 설정
    setPaymentState(prevState => ({
      ...prevState,
      resultCode: data.data.response.resultCode,
      resultMessage: data.data.response.resultMessage,
      tid: data.data.response.tid,
      orderId: data.data.response.orderId,
      mallReserved: data.data.response.mallReserved,
      // returnUrl: data.data.returnUrl,
      descriptor: data.data.display.descriptor,
      schemeGroup: data.data.response.schemeGroup,
      schemeCode: data.data.response.schemeCode,
    }));

    // Status에 따른 Paging 처리
    switch (resultStatus) {
      case 'success':
        navigate('/success');
        break;
      case 'decline':
        navigate('/decline');
        break;
      case 'processing':
        navigate('/processing');
        break;
      case 'pending':
        payType === 'vbank' || payType === 'otc'
          ? navigate('/vbankPending')
          : navigate('/pending');
        break;
      case 'failed':
        navigate('/failed');
        break;
      default:
        navigate('/error');
    }
  };

  // InnerSearch 실행
  const transactResult = useMutation(() => innerSearch(paymentState.tid), {
    onSuccess: data => {
      setIsLoading(false);
      if (data.data.returnUrl.includes('shopifyConfirm')) {
        setShopifyState(data.data.response);
        navigate('/shopifyConfirm');
      } else {
        // InnerSearch 결과처리
        payResult(data);

        // 결과 상태가 없을경우 예외처리
        if (
          data.data.response.resultStatus === null ||
          data.data.response.resultStatus === undefined
        ) {
          navigate('/error');
        }
      }
    },
    onError: error => {
      setIsLoading(false);
      navigate('/error');
    },
  });

  useEffect(() => {
    setAgreeCheckError(false);
  }, [agreeCheck]);

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const weChatCheck = /micromessenger/.test(userAgent);
    setIsWeChat(weChatCheck);

    if (sessionStorage.getItem('formSubmitted') === 'true') {
      navigate('/error');
    }

    const handleMessage = event => {
      const data = event.data;
      if (event.origin === window.location.origin) {
        if (typeof data === 'object' && data.approvalNo && data.payUrl) {
          setPaymentState(prevState => ({
            ...prevState,
            approvalNo: event.data.approvalNo,
            payUrl: event.data.payUrl,
          }));
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    const onMessage = (evt: MessageEvent) => {
      // 자식 도메인 화이트리스트

      if (!allowedChildOrigins.includes(evt.origin)) return;

      const data = evt.data as BridgeEvent;
      if (!data || data.type !== 'POPUP_REDIRECT') return;

      // console.log('[Parent] received:', data);

      // TODO: payload로 후속 처리 (예: code 교환, 상태 업데이트 등)
      // ...
      if (data.payload.state) {
        const response = JSON.parse(data.payload.state);
        setPaymentState(prev => ({
          ...prev,
          resultCode: response.resultCode,
          resultStatus: response.resultStatus,
          resultMessage: response.resultMessage,
          orderId: response.orderId,
          mallReserved: response.mallReserved,
          tid: response.tid,
          returnUrl: response.returnUrl,
        }));
        try {
          window.sessionStorage.setItem('isError', 'true');
          if (response.returnUrl.includes('shopifyConfirm')) {
            navigate('/shopifyConfirm');
          } else {
            navigate('/error');
          }
        } catch {}
      }
      setPopup(null);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [popup]);

  useEffect(() => {
    if (paymentData) {
      // WeChat인 경우 "WECHAT-H5"를 제거하고, 그렇지 않으면 "WECHAT-OA"를 제거
      let newData = { ...paymentData };

      if (newData.apm && newData.apm.CN) {
        if (isWeChat) {
          // isWeChat이 true인 경우, "WECHAT-H5"를 필터링
          newData.apm.CN = newData.apm.CN.filter(
            item => item.schemeCd !== 'WECHAT-H5',
          );
        } else {
          // isWeChat이 false인 경우, "WECHAT-OA"를 필터링
          newData.apm.CN = newData.apm.CN.filter(
            item => item.schemeCd !== 'WECHAT-OA',
          );
        }
      }
      // 필터링된 데이터를 상태에 업데이트
      setFilterdPaymentData(newData);
    }
  }, [isWeChat, paymentData]);

  useEffect(() => {
    if (paymentState && paymentState.mid !== '' && paymentState.tid === '') {
      const payReqBody: MethodListRequest = {
        mid: paymentState.mid,
        orderId: paymentState.orderId,
        productName: paymentState.productName,
        requestCurrency: paymentState.requestCurrency,
        requestAmount: paymentState.requestAmount,
        webhookUrl: paymentState.webhookUrl,
        returnUrl: paymentState.returnUrl,
        mallReserved: paymentState.mallReserved,
        customerIp: paymentState.clientIp,
        customerId: paymentState.customerId,
        isMobile: isMobile ? 'Y' : 'N',
        billkeyReq: paymentState.billkeyReq ? paymentState.billkeyReq : 'N',
        appScheme: paymentState.appScheme ? paymentState.appScheme : '',
        returnType: returnType,
      };
      setPaymentRequestBody(payReqBody);
    }
  }, [paymentState]);

  useEffect(() => {
    if (paymentRequestBody && paymentRequestBody.mid !== '') {
      getPaymentList.mutate();
    }
  }, [paymentRequestBody]);

  useEffect(() => {
    if (filterdPaymentData) {
      if (filterdPaymentData.global?.GO?.length > 0) {
        setIsGlobal(true);
        setGlobalPayment(filterdPaymentData.global.GO);
      }
      if (filterdPaymentData.card.GO && filterdPaymentData.card.GO.length > 0) {
        setCard(filterdPaymentData.card.GO);
      }
      if (filterdPaymentData.card.KR && filterdPaymentData.card.KR.length > 0) {
        setKoreanCardList(filterdPaymentData.card.KR);
      }
      if (filterdPaymentData.card.VN && filterdPaymentData.card.VN.length > 0) {
        setVietnamCardList(filterdPaymentData.card.VN);
      }
      setApmList(filterdPaymentData.apm);
      setBankTransferList(filterdPaymentData.bankTransfer);
      if (
        (filterdPaymentData.card.KR && filterdPaymentData.card.KR.length > 0) ||
        (filterdPaymentData.card.GO && filterdPaymentData.card.GO.length > 0) ||
        (filterdPaymentData.card.VN && filterdPaymentData.card.VN.length > 0)
      ) {
        setSelectedPayment('credit');
      } else if (isEmptyObject(filterdPaymentData.apm)) {
        setSelectedPayment('apm');
      } else if (isEmptyObject(filterdPaymentData.bankTransfer)) {
        setSelectedPayment('bank');
      } else {
        setSelectedPayment('global');
      }
    }
  }, [filterdPaymentData]);

  useEffect(() => {
    if (tid) {
      setPaymentState(prevState => ({ ...prevState, tid: tid }));
    }
  }, [tid]);

  const browserTypeJson = (lang: string) => {
    switch (lang) {
      case 'zh-TW':
        return 'cntr';
      case 'zh-CN':
        return 'cnsi';
      case 'en-US':
        return 'en';
      case 'ko':
        return 'ko';
      case 'ko-KR':
        return 'ko';
      case 'ja':
        return 'jp';
      default:
        return '';
    }
  };

  useEffect(() => {
    // langUse 사용유무에 따라 Y/N
    // Y > BO에 설정된 언어로 초기언어 설정
    // N > 브라우저 언어설정을 language로 설정 이외 언어시 BO 설정된 언어로 로딩
    if (language) {
      if (langUse === 'Y') {
        i18n.changeLanguage(language);
      } else {
        const userLanguage = navigator.language;
        const browserLanguage = browserTypeJson(userLanguage);
        if (browserLanguage !== '') {
          i18n.changeLanguage(browserLanguage);
          return;
        }
        i18n.changeLanguage(language);
      }
    } else {
      const userLanguage = navigator.language;
      const browserLanguage = browserTypeJson(userLanguage);
      if (browserLanguage !== '') {
        i18n.changeLanguage(browserLanguage);
        return;
      } else {
        i18n.changeLanguage('en');
      }
    }
  }, [language, langUse]);

  const handleClickTabAction = payment => {
    setSelectedPayment(payment);
    setSelectScheme({
      schemeDisplayNm: '',
      partnerCurr: '',
      partnerCd: '',
      schemeCd: '',
      schemeGroup: '',
      approvalAmount: 0,
    });
  };

  const handleAllClose = e => {
    e.preventDefault();

    const returnObject = {
      resultStatus: 'FAILED',
      resultCode: 'FAIL',
      resultMessage: 'Unprocessed payment.',
      tid: paymentState.tid,
      orderId: paymentState.orderId,
      mallReserved: paymentState.mallReserved,
      returnUrl: paymentState.returnUrl,
      schemeGroup: paymentState.schemeGroup,
      schemeCode: paymentState.schemeCode,
    };
    if (isMobile) {
      if (paymentState.returnUrl.includes('shopifyConfirm')) {
        window.location.href = paymentState.cancelUrl!;
      } else {
        const url = new URL(paymentState.returnUrl);
        const params = new URLSearchParams(url.search);
        params.set('resultStatus', 'FAILED');
        params.set(
          'resultCode',
          paymentState.resultCode ? paymentState.resultCode : '',
        );
        params.set(
          'resultMessage',
          paymentState.resultMessage ? paymentState.resultMessage : '',
        );
        params.set('tid', paymentState.tid);
        params.set('orderId', paymentState.orderId);
        params.set(
          'mallReserved',
          paymentState?.mallReserved ? paymentState.mallReserved : '',
        );
        params.set(
          'schemeGroup',
          paymentState?.schemeGroup ? paymentState.schemeGroup : '',
        );
        params.set(
          'schemeCode',
          paymentState?.schemeCode ? paymentState.schemeCode : '',
        );
        window.location.href = url + '?' + params;
      }
    } else {
      window.sessionStorage.removeItem('auth');
      window.sessionStorage.removeItem('mid');
      window.sessionStorage.removeItem('clientKey');
      window.sessionStorage.removeItem('isError');
      IframeBridge.sendViewOff(returnObject);
    }
  };

  function checkPopupClosed(popup) {
    if (popup && popup.closed) {
      clearInterval(checkPopupTimer);
      const ErrorKey = window.sessionStorage.getItem('isError');
      if (ErrorKey !== 'true') {
        transactResult.mutate();
      }
    }
  }

  const handleTermsOrPrivacy = content => {
    if (content === 'SERVICE') {
      openModal(TermsModal, {
        onSubmit: () => {},
      });
    } else {
      openModal(PrivacyPolicyModal, {
        onSubmit: () => {},
      });
    }
  };

  const handleSubmitPaypal = (schemeCd: string) => {
    const apmBody: RequestPVKRForm = {
      schemeCode: selectScheme.schemeCd,
      authorization: `Bearer ${paymentState.auth}`,
      amount: paymentState.requestAmount,
      clientKey: paymentState.clientKey,
      currency: paymentState.requestCurrency,
      mid: paymentState.mid,
      orderId: paymentState.orderId,
      productName: paymentState.productName,
      reqDate: paymentState.reqDate,
      webhookUrl: paymentState.webhookUrl,
      returnUrl: paymentState.returnUrl,
      sign: paymentState.sign,
      tid: paymentState.tid,
      isMobile: isMobile ? 'Y' : 'N',
      displayType: 'UI',
      customerIp: paymentState.clientIp,
      mallReserved: paymentState.mallReserved,
      appScheme: paymentState.appScheme,
    };
    openModal(CustomModal, {
      message: t('open_apm_app', { name: selectScheme.schemeDisplayNm }),
      isShowTwoButton: true,
      onSubmit: async () => {
        setIsLoading(true);
        if (isMobile) {
          navigate(`/formSubmit?schemeGroup=${selectScheme.schemeGroup}`, {
            // replace: true,
            state: { formData: apmBody },
          });
        } else {
          const popup = window.open(
            `/formSubmit?schemeGroup=${selectScheme.schemeGroup}`,
            '_blank',
            'width=700, height=850',
          );
          setPopup(popup);
          if (popup) {
            setTimeout(() => {
              popup?.postMessage(
                { eventType: 'formData', data: apmBody },
                window.location.origin,
              );
            }, 1000);
          }
          checkPopupTimer = setInterval(checkPopupClosed, 1000, popup);
        }
      },
    });
  };

  const ApplePayGlobal = () => {
    const submitFormData: ApplePayForm = {
      authorization: `Bearer ${paymentState.auth}`,
      amount: paymentState.requestAmount,
      clientKey: paymentState.clientKey,
      currency: paymentState.requestCurrency,
      mid: paymentState.mid,
      orderId: paymentState.orderId,
      productName: paymentState.productName,
      reqDate: paymentState.reqDate,
      webhookUrl: paymentState.webhookUrl,
      returnUrl: paymentState.returnUrl,
      sign: paymentState.sign,
      isMobile: isMobile ? 'Y' : 'N',
      mallReserved: paymentState.mallReserved,
      tid: paymentState.tid,
      displayType: 'UI',
    };
    if (isMobile) {
      navigate('/formSubmit?schemeGroup=applepay', {
        state: { formData: submitFormData },
      });
    } else {
      setIsLoading(true);
      const popup = window.open(
        '/formSubmit?schemeGroup=applepay',
        '_blank',
        'width=700, height=850',
      );
      if (popup) {
        setTimeout(() => {
          popup?.postMessage(
            { eventType: 'formData', data: submitFormData },
            window.location.origin,
          );
        }, 1000);
      }
      checkPopupTimer = setInterval(checkPopupClosed, 1000, popup);
    }
  };

  const ApplePayKorea = () => {
    const apmBody: RequestPVKRForm = {
      schemeCode: 'APPLEPAY',
      authorization: `Bearer ${paymentState.auth}`,
      amount: paymentState.requestAmount,
      clientKey: paymentState.clientKey,
      currency: paymentState.requestCurrency,
      mid: paymentState.mid,
      orderId: paymentState.orderId,
      productName: paymentState.productName,
      reqDate: paymentState.reqDate,
      webhookUrl: paymentState.webhookUrl,
      returnUrl: paymentState.returnUrl,
      sign: paymentState.sign,
      tid: paymentState.tid,
      isMobile: isMobile ? 'Y' : 'N',
      displayType: 'UI',
      customerIp: paymentState.clientIp,
      mallReserved: paymentState.mallReserved,
      appScheme: paymentState.appScheme,
    };

    setIsLoading(true);
    if (isMobile) {
      navigate(`/formSubmit?schemeGroup=PVKR`, {
        // replace: true,
        state: { formData: apmBody },
      });
    } else {
      const popup = window.open(
        `/formSubmit?schemeGroup=PVKR`,
        '_blank',
        'width=700, height=850',
      );
      setPopup(popup);
      if (popup) {
        setTimeout(() => {
          popup?.postMessage(
            { eventType: 'formData', data: apmBody },
            window.location.origin,
          );
        }, 1000);
      }
      checkPopupTimer = setInterval(checkPopupClosed, 1000, popup);
    }
  };

  const getApplePayInApmKR = data => {
    return data.apm?.KR?.find(scheme => scheme.schemeCd === 'APPLEPAY');
  };

  const handleSubmitApplepay = (schemeCd: string) => {
    if (getApplePayInApmKR(paymentData)) {
      if (schemeCd === 'APPLEPAY-GLOBAL' && i18n.language === 'ko') {
        openModal(CustomModal, {
          message: `Apple Pay에 등록된 카드 유형을 선택해주세요. `,
          isShowTwoButton: true,
          customTwoButton: true,
          onSubmit: async () => ApplePayKorea(), //애플페이 글로벌 버튼
          onCancel: async () => ApplePayGlobal(), //애플페이 PVKR 버튼
        });
      } else {
        openModal(CustomModal, {
          message: t('open_apm_app', { name: selectScheme.schemeDisplayNm }),
          isShowTwoButton: true,
          onSubmit: async () => {
            ApplePayGlobal();
          },
        });
      }
    } else {
      openModal(CustomModal, {
        message: t('open_apm_app', { name: selectScheme.schemeDisplayNm }),
        isShowTwoButton: true,
        onSubmit: async () => {
          ApplePayGlobal();
        },
      });
    }
  };

  const payGlobal = async schemeCd => {
    if (!agreeCheck) {
      // 약관동의 체크하지 않은 경우
      setAgreeCheckError(true);
      openModal(ErrorModal, {
        message: t('error_agree_check'),
      });

      foucsTerms.current?.scrollIntoView({ behavior: 'smooth' });

      return;
    }
    setAgreeCheckError(false);

    if (schemeCd === 'APPLEPAY-GLOBAL') {
      handleSubmitApplepay(schemeCd);
    } else {
      handleSubmitPaypal(schemeCd);
    }
  };

  return (
    <>
      <PopupReturnBridge
        popup={popup}
        match={url => {
          return url.startsWith(`/returnBridge`);
        }}
        onMatch={({ url, payload }) => {
          // payload: 자식이 전달한 데이터(예: code/state 등)
          console.log('Matched redirect:', url, payload);
          // TODO: 필요한 후속 처리(토큰 교환, UI 업데이트 등)
        }}
        allowChildOrigins={allowedChildOrigins}
        sameOriginPollMs={0} // 동일 오리진이 아니므로 0
        closeOnMatch={false}
        timeoutMs={2 * 60 * 1000}
        onPopupClose={() => console.log('Popup closed or timeout')}
        debug
      />
      <div className="a-section">
        <PriceContent
          selectedCurrency={selectScheme.partnerCurr}
          selectedValue={selectScheme.approvalAmount}
          requestCurrency={paymentState.requestCurrency}
          requestValue={Number(paymentState.requestAmount)}
        />
      </div>
      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <>
          {isGlobal && globalPayment && (
            <TabGlobal
              GlobalData={globalPayment}
              selectedScheme={selectScheme}
              setSelectedScheme={setSelectScheme}
              setSelectedPayment={setSelectedPayment}
              setLoading={setIsLoading}
              handleClosed={handleAllClose}
              setPopup={setPopup}
              isGlobal={isGlobal}
            />
          )}
          <div className="b-section">
            <div
              className="tab"
              style={{ paddingTop: isGlobal ? '0px' : '10px' }}
            >
              {filterdPaymentData && (
                <ul className="content">
                  {(card.length > 0 ||
                    koreanCardList.length > 0 ||
                    vietnamCardList.length > 0) && (
                    <li
                      className={
                        selectedPayment === 'credit'
                          ? 'tab-class current'
                          : 'tab-class'
                      }
                      onClick={() => handleClickTabAction('credit')}
                    >
                      {t('credit_card')}
                    </li>
                  )}
                  {isEmptyObject(apmList) && (
                    <li
                      className={
                        selectedPayment === 'apm'
                          ? 'tab-class current'
                          : 'tab-class'
                      }
                      onClick={() => handleClickTabAction('apm')}
                    >
                      {t('apm')}
                    </li>
                  )}
                  {isEmptyObject(bankTransferList) && (
                    <li
                      className={
                        selectedPayment === 'bank'
                          ? 'tab-class current'
                          : 'tab-class'
                      }
                      onClick={() => handleClickTabAction('bank')}
                    >
                      {t('bank_transfer')}
                    </li>
                  )}
                </ul>
              )}
            </div>
            {selectedPayment === 'global' && (
              <div className="tab">
                <div
                  className="tab-on current"
                  style={{
                    height: isMobileSize
                      ? 'calc((var(--vh, 1vh) * 100) - 412px)'
                      : 186,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                    }}
                  >
                    <text
                      style={{
                        color: '#212121',
                        fontWeight: 600,
                        fontSize: 16,
                      }}
                    >
                      {t('click')}
                      <text style={{ color: '#F2682D' }}>{` '${t(
                        'pay_now',
                      )}' `}</text>
                      {t('toproceedwithyourpayment')}
                    </text>
                    <text
                      style={{
                        color: '#212121',
                        fontWeight: 600,
                        fontSize: 16,
                      }}
                    >
                      {t('withyourpayment')}
                    </text>
                    <text
                      style={{
                        color: '#616161',
                        fontWeight: 500,
                        fontSize: 16,
                        paddingTop: 6,
                      }}
                    >
                      {t('pleasecomplete')}
                    </text>
                  </div>
                </div>
              </div>
            )}
            {selectedPayment === 'credit' && (
              <TabCredit
                creditData={card}
                keypadToken={keypadToken}
                koreanCardData={koreanCardList}
                vietnamCardData={vietnamCardList}
                selectedScheme={selectScheme}
                setSelectedScheme={setSelectScheme}
                setLoading={setIsLoading}
                handleClosed={handleAllClose}
                setPopup={setPopup}
                isGlobal={isGlobal}
              />
            )}
            {selectedPayment === 'apm' && (
              <TabApm
                data={apmList}
                selectedScheme={selectScheme}
                setSelectedScheme={setSelectScheme}
                setLoading={setIsLoading}
                handleClosed={handleAllClose}
                setPopup={setPopup}
                isGlobal={isGlobal}
              />
            )}
            {selectedPayment === 'bank' && (
              <TabBankTransfer
                data={bankTransferList}
                selectedScheme={selectScheme}
                setSelectedScheme={setSelectScheme}
                setLoading={setIsLoading}
                handleClosed={handleAllClose}
                setPopup={setPopup}
                isGlobal={isGlobal}
              />
            )}
            {selectedPayment === 'global' && (
              <>
                <div className="agreeFloatBox">
                  <div className="label" ref={foucsTerms}>
                    <div className="check">
                      <input
                        type="checkbox"
                        id={AgreeCheckError ? 'checkbox error' : 'checkbox'}
                        name="agree"
                        checked={agreeCheck}
                        onChange={e => {
                          setAgreeCheck(e.target.checked);
                        }}
                      />

                      <label
                        htmlFor={
                          AgreeCheckError ? 'checkbox error' : 'checkbox'
                        }
                      ></label>
                    </div>
                    <div className="columnBox">
                      <div className="text">
                        <div
                          onClick={() => setAgreeCheck(!agreeCheck)}
                          style={{ cursor: 'pointer' }}
                        >
                          {t('start_agree_terms')}
                          {t('terms_and_conditions')}
                          {t('and_the')}
                          {t('privacy_policy')}
                          {t('end_agree_terms')}
                        </div>
                        <img
                          className={`arrow ${open ? 'open' : ''}`}
                          src={ArrowPng}
                          alt=""
                          onClick={() => setOpen(!open)}
                        />
                      </div>
                      <div className={`text toggleRow ${open ? 'open' : ''}`}>
                        <p className="dot">●</p>
                        <span
                          className="link"
                          onClick={() => handleTermsOrPrivacy('SERVICE')}
                        >
                          {t('terms_and_conditions')}
                        </span>
                      </div>
                      <div className={`text toggleRow ${open ? 'open' : ''}`}>
                        <p className="dot">●</p>
                        <span
                          className="link"
                          onClick={() => handleTermsOrPrivacy('PRIVACY')}
                        >
                          {t('privacy_policy')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="c-section">
                  <div className="box">
                    <button className="all-close" onClick={handleAllClose}>
                      {t('cancel')}
                    </button>

                    {selectScheme.schemeCd === 'APPLEPAY-GLOBAL' && (
                      <button
                        className="orange"
                        onClick={() => payGlobal('APPLEPAY-GLOBAL')}
                      >
                        {t('pay_now')}
                      </button>
                    )}
                    {selectScheme.schemeCd === 'PAYPAL' && (
                      <button
                        className="orange"
                        onClick={() => payGlobal('PAYPAL')}
                      >
                        {t('pay_now')}
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default PaymentPage;
